/**
 * Shared UK bank-detail helpers for the Direct Debit capture forms.
 *
 * Used by the contract-signing DD step (SignContract) and the standalone
 * DD setup page (SetupDirectDebit) so both behave identically:
 *   - inputs are constrained to digits and capped in length (can't type too many)
 *   - a specific, per-field message tells the customer exactly what's wrong
 *
 * The backend (contract_signing._clean_bank_inputs) is the source of truth and
 * enforces the same 6-digit sort code / 8-digit account rules; these helpers just
 * make the requirement visible before submit instead of silently disabling the button.
 */

/** Strip everything except digits. */
export function bankDigits(value: string): string {
  return value.replace(/\D/g, '')
}

/** Digits only, capped at 6, formatted as XX-XX-XX. */
export function formatSortCode(value: string): string {
  const digits = bankDigits(value).slice(0, 6)
  if (digits.length > 4) return `${digits.slice(0, 2)}-${digits.slice(2, 4)}-${digits.slice(4)}`
  if (digits.length > 2) return `${digits.slice(0, 2)}-${digits.slice(2)}`
  return digits
}

/** Digits only, capped at 8. */
export function formatAccountNumber(value: string): string {
  return bankDigits(value).slice(0, 8)
}

/**
 * Inline error for the sort code field, or '' when it's empty or complete.
 * (Input is capped at 6 digits, so only the "too short" case can occur.)
 */
export function sortCodeError(value: string): string {
  const d = bankDigits(value)
  if (d.length === 0 || d.length === 6) return ''
  return `Sort code must be 6 digits — you've entered ${d.length}.`
}

/**
 * Inline error for the account number field, or '' when it's empty or complete.
 * (Input is capped at 8 digits, so only the "too short" case can occur.)
 */
export function accountNumberError(value: string): string {
  const d = bankDigits(value)
  if (d.length === 0 || d.length === 8) return ''
  return `Account number must be 8 digits — you've entered ${d.length}.`
}

/** Whether sort code + account number are both the correct length. */
export function bankFieldsComplete(sortCode: string, accountNumber: string): boolean {
  return bankDigits(sortCode).length === 6 && bankDigits(accountNumber).length === 8
}
